import React, { useState, useEffect, useRef, useMemo } from 'react'
import FullCalendar from '@fullcalendar/react' // must go before plugins
import dayGridPlugin from '@fullcalendar/daygrid' // a plugin!
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import axios from 'axios'
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import TextField from '@mui/material/TextField'
import { toast } from 'react-toastify'
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Autocomplete from '@mui/material/Autocomplete';
import FormHelperText from '@mui/material/FormHelperText';
import Popover from '@mui/material/Popover';
import Badge from '@mui/material/Badge';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useAuth } from '../Auth/AuthContext';
import moment from 'moment-timezone'
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import '../App.css'
import SendPush from './SendPush';
import PositionsEditor from './PositionsEditor';
import { defaultPositionsForEventType, rosterLabel, eventHasApplicants } from '../Constants/Instruments';
import AdminRotaModal from './AdminRotaModal';

const EVENT_CATEGORIES = ['Solo', 'Duo', 'DJ', 'Dancer', 'Band']

const eventHasArtist = (event, artistId) => {
  if (!artistId || artistId === 'all') return true
  if (String(event.artistId) === String(artistId)) return true
  return (event.positions || []).some((p) => String(p.artistId) === String(artistId))
}

const eventFigures = (gigs) => {
  const cashTurnover = gigs
    .filter((gig) => gig.venueFee < gig.artistFee)
    .reduce((sum, gig) => sum + gig.venueFee + gig.artistFee, 0)
  const invoiceTurnover = gigs
    .filter((gig) => gig.venueFee >= gig.artistFee)
    .reduce((sum, gig) => sum + gig.venueFee, 0)
  const turnover = cashTurnover + invoiceTurnover
  const cogs = gigs.reduce((sum, gig) => sum + gig.artistFee, 0)
  return {
    gigs: gigs.length,
    grossTurnover: Math.round(turnover * 1.2),
    netTurnover: turnover,
    grossProfit: turnover - cogs,
  }
}

const figureRows = (stats, countLabel) => [
  { label: countLabel, value: stats.gigs, emphasis: true },
  { label: 'Gross turnover', value: `£${stats.grossTurnover}` },
  { label: 'Net turnover', value: `£${stats.netTurnover}` },
  { label: 'Gross profit', value: `£${stats.grossProfit}` },
]

export default function Calendar() {

  const calendarRef = useRef(null);
  const calendarWrapRef = useRef(null);
  const { currentUser, venue, setVenue, isAdmin, isDancerAdmin } = useAuth();

  const [events, setEvents] = useState([])
  const [artistFilter, setArtistFilter] = useState('all')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [applied, setApplied] = useState([])
  const [giggingArtists, setGiggingArtists] = useState([]);
  const [title, setTitle] = useState("")
  const [eventType, setEventType] = useState("")
  const [artistFee, setArtistFee] = useState("")
  const [venueFee, setVenueFee] = useState("")
  const [notes, setNotes] = useState("")
  const [setTimes, setSetTimes] = useState("")
  const [equipment, setEquipment] = useState("")
  const [id, setId] = useState("")
  const [newItem, setNewItem] = useState("")
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState("")
  const [eventDate, setEventDate] = useState("")
  const [startOfMonth, setStartOfMonth] = useState("");
  const [endOfMonth, setEndOfMonth] = useState("");
  const [artistObj, setArtistObj] = useState("")
  const [open, setOpen] = useState(false);
  const [venues, setVenues] = useState([]);
  //Artist select
  const [artists, setArtists] = useState([])
  const [artist, setArtist] = useState("")
  const [artistName, setArtistName] = useState("")
  const [artistEmail, setArtistEmail] = useState("")
  const [artistNumber, setArtistNumber] = useState("")
  const [artistImg, setArtistImg] = useState("")
  const [positions, setPositions] = useState([])

  //Push modal
  const [pushOpen, setPushOpen] = useState(false)
  const [filterAnchor, setFilterAnchor] = useState(null)
  const filtersOpen = Boolean(filterAnchor)
  const [figuresAnchor, setFiguresAnchor] = useState(null)
  const figuresOpen = Boolean(figuresAnchor)

  // Admin rota mode (full admins only)
  const [calendarMode, setCalendarMode] = useState('events')
  const [rotaEntries, setRotaEntries] = useState([])
  const [rotaModalOpen, setRotaModalOpen] = useState(false)
  const [rotaDateStr, setRotaDateStr] = useState('')
  const [rotaEntry, setRotaEntry] = useState(null)

  const isRotaMode = isAdmin && calendarMode === 'rota'
  // Keep a ref so FullCalendar callbacks always see the current mode
  const isRotaModeRef = useRef(isRotaMode)
  const rotaEntriesRef = useRef(rotaEntries)
  isRotaModeRef.current = isRotaMode
  rotaEntriesRef.current = rotaEntries

  const handleChange = (event) => {
    setVenue(event.target.value);
  };

  const handleClose = () => {
    setOpen(false);
    setArtist("");
    setSetTimes([]);
    setEquipment([]);
    setNotes("");
    setArtistFee("");
    setVenueFee("");
    setEventType("")
    setPositions([])
  }

  useEffect(() => {
    if (!startOfMonth || !endOfMonth) return
    if (isRotaMode) {
      getRotaEntries()
    } else {
      getEvents()
    }
  }, [startOfMonth, endOfMonth, isRotaMode])

  const getEvents = () => {
    axios.post(`${process.env.REACT_APP_API}/event/get-events-by-date-range`, { startOfMonth, endOfMonth })
      .then((res) => {
        // Dancer admins only see dancer events; stats and venue filtering derive from this state
        setEvents(isDancerAdmin ? res.data.filter((event) => event.eventType === 'Dancer') : res.data)
      })
      .catch((e) => {
        console.log(e)
      })
  }

  const getRotaEntries = () => {
    if (!currentUser?.accessToken) return
    axios.post(
      `${process.env.REACT_APP_API}/admin-rota/by-date-range`,
      { startOfMonth, endOfMonth },
      { headers: { AuthToken: currentUser.accessToken } }
    )
      .then((res) => setRotaEntries(res.data || []))
      .catch((e) => {
        console.log(e)
        toast.error('Failed to load admin rota')
      })
  }

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (venue !== 'all' && venue && String(event.venueId) !== String(venue)) return false
      if (!eventHasArtist(event, artistFilter)) return false
      if (categoryFilter !== 'all' && event.eventType !== categoryFilter) return false
      return true
    })
  }, [events, venue, artistFilter, categoryFilter])

  const selectedArtist = useMemo(
    () => artists.find((a) => a._id === artistFilter) || null,
    [artists, artistFilter]
  )

  const activeFilterCount = [
    isAdmin && calendarMode !== 'events',
    !isRotaMode && venue && venue !== 'all',
    !isRotaMode && artistFilter && artistFilter !== 'all',
    !isRotaMode && categoryFilter && categoryFilter !== 'all',
  ].filter(Boolean).length

  const clearCalendarFilters = () => {
    if (isAdmin) setCalendarMode('events')
    setVenue('all')
    setArtistFilter('all')
    setCategoryFilter('all')
  }

  const calendarEvents = useMemo(() => {
    if (!isRotaMode) {
      return filteredEvents
    }
    return rotaEntries.map((entry) => ({
      id: entry._id,
      title: entry.adminName || entry.adminEmail || 'On call',
      start: entry.dateStr,
      allDay: true,
      extendedProps: {
        ...entry,
        isRota: true,
      },
    }))
  }, [isRotaMode, filteredEvents, rotaEntries])

  useEffect(() => {
    if (!artist) {
      setArtistName("");
      setArtistEmail("");
      setArtistNumber("")
      setArtistImg("")
    } else {
      axios.post(`${process.env.REACT_APP_API}/artist/get-artist`, { _id: artist }, { headers: { 'AuthToken': currentUser.accessToken } })
        .then((res) => {
          let { email, img, name, number } = res.data;
          setArtistObj(res.data)
          setArtistName(name);
          setArtistEmail(email);
          setArtistNumber(number)
          setArtistImg(img)
        })
        .catch((e) => {
          console.log(e)
        })
    }
  }, [artist])

  useEffect(() => {
    getEvents();
    getArtists();
    getVenues();
  }, [])

  const monthStats = useMemo(() => eventFigures(filteredEvents), [filteredEvents])
  const dancerStats = useMemo(
    () => eventFigures(filteredEvents.filter((event) => event.eventType === 'Dancer')),
    [filteredEvents]
  )
  const dancerMngmtFee = Math.round(dancerStats.grossProfit * 0.5)
  const monthFigures = figureRows(monthStats, 'Gigs')
  const dancerFigures = [
    ...figureRows(dancerStats, 'Dancers'),
    { label: 'Dancer Mngmt fee', value: `£${dancerMngmtFee}` },
  ]
  const adjustedGrossProfit = monthStats.grossProfit - dancerMngmtFee
  const figuresMonthLabel = startOfMonth ? moment(startOfMonth).format('MMMM YYYY') : ''

  const getVenues = () => {
    axios.get(`${process.env.REACT_APP_API}/venue/get-venues`, { headers: { 'AuthToken': currentUser.accessToken } })
      .then((res) => { setVenues(res.data) })
      .catch((e) => { console.log(e); })

  }

  const getArtists = () => {
    axios.get(`${process.env.REACT_APP_API}/artist/get-artists`, { headers: { 'AuthToken': currentUser.accessToken } })
      .then((res) => {
        let available = isDancerAdmin ? res.data.filter((artist) => artist.dancer === true) : res.data
        let sorted = available.sort((a, b) => a.name !== b.name ? a.name < b.name ? -1 : 1 : 0)

        setArtists(sorted)
      })
      .catch((e) => console.log(e))
  }

  const handleChipDelete = (itemToDelete) => () => {
    setEquipment((item) => item.filter((item) => item.key !== itemToDelete.key));
  };
  const addItem = (e) => {
    if (e.key === 'Enter') {
      console.log(newItem)
      setEquipment([...equipment, { key: equipment[equipment.length - 1].key + 1, label: newItem }])
      setNewItem("")
    }
  }
  const removeSet = (set) => {
    if (!window.confirm(`Delete set ${set.from} - ${set.to}?`)) return
    setSetTimes((item) => item.filter((item) => item.key !== set.key));
  }

  const moveSet = (index, direction) => {
    const next = index + direction
    if (!setTimes || next < 0 || next >= setTimes.length) return
    const reordered = [...setTimes]
    const [item] = reordered.splice(index, 1)
    reordered.splice(next, 0, item)
    setSetTimes(reordered)
  }

  const addSet = () => {
    if (!from || !to) {
      toast.error("Please add from and to time")
    } else {
      let key = setTimes.length < 1 ? 1 : setTimes[setTimes.length - 1].key + 1
      setSetTimes([...setTimes, { key, from, to }])
      setTo(""); setFrom("")
    }
  }

  const openRotaModal = (dateStr, entry = null) => {
    setRotaDateStr(dateStr)
    setRotaEntry(entry)
    setRotaModalOpen(true)
  }

  const handleDateClick = (info) => {
    if (!isRotaModeRef.current) return
    const dateStr = info.dateStr
    const existing = rotaEntriesRef.current.find((entry) => entry.dateStr === dateStr)
    openRotaModal(dateStr, existing || null)
  }

  const handleClick = (info) => {
    if (isRotaMode || info.event.extendedProps?.isRota) {
      const entry = info.event.extendedProps
      openRotaModal(entry.dateStr, {
        _id: entry._id,
        adminUid: entry.adminUid,
        adminName: entry.adminName,
        adminEmail: entry.adminEmail,
        adminPhone: entry.adminPhone,
        dateStr: entry.dateStr,
      })
      return
    }

    const { title } = info.event;
    const { artistId, setTimes, equipment, artistFee, venueFee, notes, _id, applied, eventType, dateStr, positions: eventPositions } = info.event.extendedProps;
    let artistsGigging = events.filter((e) => e.dateStr === dateStr).flatMap((e) => {
      const ids = []
      if (e.artistId) ids.push(e.artistId)
      ;(e.positions || []).forEach((p) => { if (p.artistId) ids.push(p.artistId) })
      return ids
    })

    setOpen(true); setEventDate(dateStr)
    setDate(moment(info.event.start).format('YYYY-MM-DD'))
    setTitle(title);
    setArtistFee(artistFee);
    setVenueFee(venueFee);
    setNotes(notes);
    setSetTimes(setTimes);
    setEquipment(equipment); setId(_id); setArtist(artistId);
    setApplied(applied || []);
    setGiggingArtists(artistsGigging);
    setEventType(eventType)
    setPositions(eventPositions?.length ? eventPositions : [])
  }

  const deleteEvent = () => {
    if (window.confirm("Are you sure you want to delete event?")) {
      axios.post(`${process.env.REACT_APP_API}/event/delete`, { _id: id }, { headers: { 'AuthToken': currentUser.accessToken } })
        .then((res) => {
          toast.success("Successfully deleted")
          handleClose(); getEvents();
        })
        .catch((e) => console.log(e))
    }
  }

  const updateEvent = () => {
    const payload = {
      start: moment(`${date}T${setTimes[0].from}`).tz("Europe/London").format(),
      artistFee,
      artistImg,
      venueFee,
      setTimes,
      equipment,
      eventType,
      notes,
      artistId: artist,
      artistName,
      artistEmail,
      artistNumber,
      artist: artistObj,
    }
    if (positions.length > 0) {
      payload.positions = positions
    }
    axios.post(`${process.env.REACT_APP_API}/event/update`,
      { _id: id, payload },
      { headers: { 'AuthToken': currentUser.accessToken } })
      .then((res) => {
        toast.success("Successfully updated")
        handleClose(); getEvents();
      })
      .catch((e) => console.log(e))
  }

  const persistRoster = (nextPositions = positions) =>
    axios.post(`${process.env.REACT_APP_API}/event/update-positions`, {
      eventId: id,
      positions: nextPositions,
      artistFee,
    }, { headers: { 'AuthToken': currentUser.accessToken } })
      .then((res) => {
        const saved = res.data.positions || nextPositions
        setPositions(saved)
        return saved
      })

  const enableRoster = () => {
    const next = defaultPositionsForEventType(eventType || 'Solo')
    setPositions(next)
    persistRoster(next)
      .then(() => {
        toast.success("Roster enabled")
        getEvents()
      })
      .catch((e) => {
        console.log(e)
        setPositions([])
        toast.error(e?.response?.data || "Failed to enable roster")
      })
  }

  const handleAssignApplicant = (positionId, artistId) => {
    persistRoster()
      .then(() => axios.post(`${process.env.REACT_APP_API}/event/assign-position`, {
        eventId: id,
        positionId,
        artistId,
      }, { headers: { 'AuthToken': currentUser.accessToken } }))
      .then((res) => {
        toast.success("Assigned — pending accept")
        setPositions(res.data.positions || [])
        getEvents()
      })
      .catch((e) => { console.log(e); toast.error(e?.response?.data || "Failed to assign") })
  }

  const handleInvite = ({ positionId, artistId, name, email, number }) => {
    persistRoster()
      .then(() => axios.post(`${process.env.REACT_APP_API}/event/invite-to-position`, {
        eventId: id,
        positionId,
        artistId,
        name,
        email,
        number,
        invitedBy: 'admin',
      }, { headers: { 'AuthToken': currentUser.accessToken } }))
      .then((res) => {
        toast.success("Invite sent")
        setPositions(res.data.positions || [])
        getEvents()
      })
      .catch((e) => { console.log(e); toast.error(e?.response?.data || "Invite failed") })
  }

  const checkArtist = () => {
    const idsToCheck = positions.length
      ? positions.filter((p) => p.artistId).map((p) => p.artistId)
      : (artist ? [artist] : [])

    if (idsToCheck.length === 0) {
      updateEvent()
      return
    }

    Promise.all(idsToCheck.map((artistId) =>
      axios.post(`${process.env.REACT_APP_API}/event/check-artist`, { artistId, date: eventDate, eventId: id }, { headers: { 'AuthToken': currentUser.accessToken } })
    ))
      .then((results) => {
        const conflicts = results.some((res) => res.data.length > 0)
        if (!conflicts) {
          updateEvent()
        } else if (window.confirm("An artist is already gigging that day — continue?")) {
          updateEvent()
        }
      })
      .catch((e) => console.log(e))
  }

  useEffect(() => {
    if (artist) {
      let info = getInfo(artist)
      setArtistName(info[0].name); setArtistEmail(info[0].email); setArtistNumber(info[0].number); setArtistImg(info[0].img)
    }
  }, [artist])

  const getInfo = (id) => {
    let info = artists.filter(choice => choice._id === id)
    return info
  }

  useEffect(() => {
    const wrap = calendarWrapRef.current
    if (!wrap) return

    let frame
    let settle
    const updateCalendarSize = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        calendarRef.current?.getApi()?.updateSize()
      })
      clearTimeout(settle)
      settle = setTimeout(() => {
        calendarRef.current?.getApi()?.updateSize()
      }, 520)
    }

    const observer = new ResizeObserver(updateCalendarSize)
    observer.observe(wrap)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(settle)
      observer.disconnect()
    }
  }, [])

  const handleDatesSet = () => {
    const calendarApi = calendarRef?.current?.getApi(); // Get FullCalendar instance
    const calendarDate = calendarApi?.getDate(); // Get the exact current date of the view

    const start = moment(calendarDate).startOf('month');
    const end = moment(calendarDate).endOf('month');

    setStartOfMonth(start.startOf('day'));
    setEndOfMonth(end.endOf('day'));
  };


  return (
    <div ref={calendarWrapRef} style={{ width: '100%' }}>
      <SendPush open={pushOpen} setOpen={setPushOpen} />
      <AdminRotaModal
        open={rotaModalOpen}
        onClose={() => {
          setRotaModalOpen(false)
          setRotaEntry(null)
          setRotaDateStr('')
        }}
        dateStr={rotaDateStr}
        existingEntry={rotaEntry}
        onSaved={getRotaEntries}
      />
      <div className="calendar-toolbar">
        <div className="calendar-toolbar-controls">
          <button
            type="button"
            className="secondary-btn calendar-filter-btn"
            aria-haspopup="true"
            aria-expanded={filtersOpen}
            onClick={(e) => setFilterAnchor(e.currentTarget)}
          >
            <Badge
              color="secondary"
              badgeContent={activeFilterCount}
              invisible={activeFilterCount === 0}
              sx={{ '& .MuiBadge-badge': { backgroundColor: '#f47fdf', color: '#fff' } }}
            >
              <FilterListIcon fontSize="small" />
            </Badge>
            <span>Filters</span>
          </button>
          {!isRotaMode && (
            <button className="primary-btn calendar-notify-btn" onClick={() => setPushOpen(true)}>
              <span className="calendar-notify-full">Send Notification</span>
              <span className="calendar-notify-short">Notify</span>
            </button>
          )}
          {!isRotaMode && (isAdmin || isDancerAdmin) && (
            <button
              type="button"
              className="secondary-btn calendar-filter-btn"
              aria-haspopup="true"
              aria-expanded={figuresOpen}
              onClick={(e) => setFiguresAnchor(e.currentTarget)}
            >
              <img
                className="calendar-figures-icon"
                src={require('../Assets/Icons/calculator-pink.png')}
                alt=""
              />
              <span>Figures</span>
            </button>
          )}
        </div>
        <Popover
          open={filtersOpen}
          anchorEl={filterAnchor}
          onClose={() => setFilterAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          PaperProps={{
            className: 'calendar-filter-popover',
            sx: { p: 2, width: 320, maxWidth: 'calc(100vw - 24px)', mt: 1, overflow: 'visible' },
          }}
        >
          <div className="calendar-filter-panel">
            {isAdmin && (
              <FormControl size="small" fullWidth>
                <InputLabel id="calendar-mode-select-label">Calendar</InputLabel>
                <Select
                  labelId="calendar-mode-select-label"
                  id="calendar-mode-select"
                  value={calendarMode}
                  label="Calendar"
                  onChange={(e) => setCalendarMode(e.target.value)}
                  MenuProps={{ disablePortal: true }}
                >
                  <MenuItem value="events">Events</MenuItem>
                  <MenuItem value="rota">Rota</MenuItem>
                </Select>
              </FormControl>
            )}

            {!isRotaMode && (
              <FormControl size="small" fullWidth>
                <InputLabel id="calendar-venue-select-label">Venue</InputLabel>
                <Select
                  labelId="calendar-venue-select-label"
                  id="calendar-venue-select"
                  value={venue}
                  label="Venue"
                  onChange={handleChange}
                  MenuProps={{ disablePortal: true }}
                >
                  <MenuItem value="all">All venues</MenuItem>
                  {venues.map((venueOption) => (
                    <MenuItem key={venueOption._id} value={venueOption._id}>{venueOption.venueName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!isRotaMode && (
              <FormControl size="small" fullWidth>
                <InputLabel id="calendar-category-select-label">Category</InputLabel>
                <Select
                  labelId="calendar-category-select-label"
                  id="calendar-category-select"
                  value={categoryFilter}
                  label="Category"
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  MenuProps={{ disablePortal: true }}
                >
                  <MenuItem value="all">All categories</MenuItem>
                  {EVENT_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>{category}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {!isRotaMode && (
              <Autocomplete
                size="small"
                options={artists}
                value={selectedArtist}
                onChange={(_, value) => setArtistFilter(value?._id || 'all')}
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, value) => option._id === value._id}
                componentsProps={{ popper: { disablePortal: true } }}
                renderInput={(params) => (
                  <TextField {...params} label="Artist" placeholder="All artists" />
                )}
              />
            )}

            {activeFilterCount > 0 && (
              <button type="button" className="calendar-filter-clear" onClick={clearCalendarFilters}>
                Clear filters
              </button>
            )}
          </div>
        </Popover>

        {!isRotaMode && (isAdmin || isDancerAdmin) && (
          <Popover
            open={figuresOpen}
            anchorEl={figuresAnchor}
            onClose={() => setFiguresAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              className: 'calendar-figures-popover',
              sx: { p: 2, width: 300, maxWidth: 'calc(100vw - 24px)', mt: 1 },
            }}
          >
            <div className="calendar-figures-panel">
              {figuresMonthLabel && (
                <p className="calendar-figures-month">{figuresMonthLabel}</p>
              )}
              {monthFigures.map((figure) => (
                <div
                  key={`all-${figure.label}`}
                  className={`calendar-figures-row${figure.emphasis ? ' calendar-figures-row-emphasis' : ''}`}
                >
                  <span>{figure.label}</span>
                  <span>{figure.value}</span>
                </div>
              ))}
              <hr className="calendar-figures-divider" />
              {dancerFigures.map((figure) => (
                <div
                  key={`dancer-${figure.label}`}
                  className={`calendar-figures-row${figure.emphasis ? ' calendar-figures-row-emphasis' : ''}`}
                >
                  <span>{figure.label}</span>
                  <span>{figure.value}</span>
                </div>
              ))}
              <hr className="calendar-figures-divider" />
              <div className="calendar-figures-row calendar-figures-row-emphasis">
                <span>Adjusted gross profit</span>
                <span>£{adjustedGrossProfit}</span>
              </div>
            </div>
          </Popover>
        )}

        {isRotaMode && (
          <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Click a day to add or edit the on-call admin
          </Typography>
        )}
      </div>

      <FullCalendar
        ref={calendarRef} // Attach the ref to the calendar
        plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
        nextDayThreshold='09:00:00'
        initialView="dayGridMonth"
        headerToolbar={{ center: 'dayGridMonth,listMonth' }}
        events={calendarEvents}
        eventClick={function (info) { handleClick(info) }}
        dateClick={handleDateClick}
        editable={false}
        selectable={isRotaMode}
        selectMirror={false}
        unselectAuto={true}
        select={(info) => {
          if (!isRotaModeRef.current) return
          const dateStr = moment(info.start).format('YYYY-MM-DD')
          const existing = rotaEntriesRef.current.find((entry) => entry.dateStr === dateStr)
          openRotaModal(dateStr, existing || null)
          info.view.calendar.unselect()
        }}
        contentHeight={'100vh'}
        firstDay={1}
        datesSet={(info) => handleDatesSet(info)} // Callback for visible range changes

        eventContent={function (info) {
          if (isRotaMode || info.event.extendedProps?.isRota) {
            const name = info.event.title || 'On call'
            const phone = info.event.extendedProps?.adminPhone || ''
            return { html: phone ? `${name} · ${phone}` : name }
          }
          let name = info.event.extendedProps.artistName ? info.event.extendedProps.artistName : ""
          const xy = rosterLabel(info.event.extendedProps.positions)
          const prefix = xy ? `${xy} ` : ''
          const setFrom = info.event.extendedProps.setTimes?.[0]?.from || ''
          return { html: `${setFrom} ${prefix}${info.event.title} - ${name}` };
        }}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          omitZeroMinute: true,
          meridiem: 'short'
        }}
        eventClassNames={(event) => {
          const props = event.event.extendedProps
          if (isRotaMode || props?.isRota) {
            return 'rota-event';
          }
          if (props.status === 'Confirmed') {
            return 'confirmed-event';
          } else if (props.status === 'Pending') {
            return 'pending-event';
          } else if (eventHasApplicants(props)) {
            return 'unassigned-event';
          }
          return 'default-event';
        }}

      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <Typography id="modal-modal-title" variant="h6" component="h2">
            {title} - {eventType}
          </Typography>

          <FormControl sx={{ mb: 2 }}>
            <RadioGroup row aria-labelledby="demo-row-radio-buttons-group-label" name="eventType" value={eventType} onChange={(e) => setEventType(e.target.value)}>
              <FormControlLabel value="Solo" control={<Radio disabled={isDancerAdmin} />} label="Solo" />
              <FormControlLabel value="Duo" control={<Radio disabled={isDancerAdmin} />} label="Duo" />
              <FormControlLabel value="DJ" control={<Radio disabled={isDancerAdmin} />} label="DJ" />
              <FormControlLabel value="Dancer" control={<Radio disabled={isDancerAdmin} />} label="Dancer" />
              <FormControlLabel value="Band" control={<Radio disabled={isDancerAdmin} />} label="Band" />
            </RadioGroup>
          </FormControl>

          {positions.length > 0 && (
          <PositionsEditor
            positions={positions}
            setPositions={setPositions}
            artists={artists}
            artistFee={artistFee}
            onAssignApplicant={handleAssignApplicant}
            onInvite={handleInvite}
            giggingArtistIds={giggingArtists}
          />
          )}

          {!positions.length && (
          <>
          <FormControl fullWidth sx={{ my: 1 }}>
            <InputLabel id="demo-simple-select-label">Artist</InputLabel>
            <Select labelId="demo-simple-select-label" id="demo-simple-select" value={artist} label="Artist" onChange={(e) => setArtist(e.target.value)}>
              <MenuItem value={""}>Select Artist</MenuItem>
              {artists.length > 0 && artists.map((artist, i) => {
                return <MenuItem key={i} value={artist._id}>{artist?.name}</MenuItem>
              })}
            </Select>
          </FormControl>
          {applied.length > 0 &&

            <FormControl fullWidth sx={{ my: 1 }}>
              <InputLabel id="demo-simple-select-label">Applied</InputLabel>

              <Select labelId="demo-simple-select-label" id="demo-simple-select" label="Applied" onChange={(e) => setArtist(e.target.value)}>
                <MenuItem value={""}>Select Artist</MenuItem>
                {applied.map((artistId, i) => {
                  const found = artists.find((a) => a._id === artistId);
                  const displayName = found ? found.name : "Unknown";
                  const isGigging = giggingArtists.includes(artistId);

                  return (
                    <MenuItem
                      key={i}
                      value={artistId}
                      sx={{
                        color: isGigging ? "red" : "inherit",
                      }}
                    >
                      {displayName}
                    </MenuItem>
                  );
                })}
              </Select>

              {applied.length > 15 ? (
                <FormHelperText sx={{ color: '#f44336', fontWeight: 600, mt: 0.5 }}>
                  More than 15 applicants
                </FormHelperText>
              ) : applied.length > 10 ? (
                <FormHelperText sx={{ color: '#ff9800', fontWeight: 600, mt: 0.5 }}>
                  More than 10 applicants
                </FormHelperText>
              ) : null}
            </FormControl>
          }
          </>
          )}

          {!positions.length && !artist && (
            <Button size="small" sx={{ mb: 1 }} onClick={enableRoster}>
              Enable roster positions (test)
            </Button>
          )}

          <TextField
            variant='outlined'
            fullWidth
            sx={{ my: 1 }}
            label='Artist Fee'
            value={artistFee}
            onChange={(e) => setArtistFee(e.target.value)}
          />

          {(isAdmin || isDancerAdmin) && (
            <TextField
              variant='outlined'
              fullWidth
              sx={{ my: 1 }}
              label='Venue Fee'
              value={venueFee}
              onChange={(e) => setVenueFee(e.target.value)}
            />
          )}

          <TextField
            variant='outlined'
            fullWidth
            sx={{ my: 1 }}
            multiline
            rows={2}
            label='Booking Notes'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />

          <label>From</label>
          <input type='time' style={{ height: '40px', width: '100px', margin: '0 5px', padding: '5px' }} value={from} onChange={(e) => setFrom(e.target.value)} />
          <label>To</label>
          <input type='time' style={{ height: '40px', width: '100px', margin: '0 5px', padding: '5px' }} value={to} onChange={(e) => setTo(e.target.value)} />
          <button onClick={addSet} style={{ backgroundColor: '#395076', color: 'white', border: 'unset', padding: '5px 10px' }}>Add</button>

          {setTimes.length > 0 && setTimes.map((set, i) => {
            return (
              <div key={set.key ?? i} style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ color: '#395076' }}>Set {i + 1}</h4>
                <p style={{ color: '#6e767b' }}>{set.from} - {set.to}</p>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <IconButton size="small" aria-label={`Move set ${i + 1} up`} disabled={i === 0} onClick={() => moveSet(i, -1)}>
                    <KeyboardArrowUpIcon />
                  </IconButton>
                  <IconButton size="small" aria-label={`Move set ${i + 1} down`} disabled={i === setTimes.length - 1} onClick={() => moveSet(i, 1)}>
                    <KeyboardArrowDownIcon />
                  </IconButton>
                  <DeleteForeverIcon color="error" onClick={() => removeSet(set)} style={{ cursor: 'pointer', marginLeft: 8 }} />
                </div>
              </div>
            )
          })}


          <Paper sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', listStyle: 'none', p: 0.5, mb: 2, }} component="ul">
            {equipment.length > 0 && equipment.map((data) => {
              let icon;
              return (
                <ListItem key={data.key}>
                  <Chip icon={icon} label={data.label} onDelete={handleChipDelete(data)} />
                </ListItem>
              );
            })}
            <input onKeyDown={addItem} value={newItem} onChange={(e) => setNewItem(e.target.value)} />
          </Paper>
          <Button variant='contained' sx={{ mx: 1, backgroundColor: '#395076' }} onClick={checkArtist}>Update</Button>

          <Button variant='contained' sx={{ mx: 1 }} color='error' onClick={deleteEvent}>Delete</Button>

        </Box>
      </Modal>
    </div>
  )
}

const ListItem = styled('li')(({ theme }) => ({
  margin: theme.spacing(0.5),
}));


const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 480,
  maxWidth: '95%',
  maxHeight: '80%',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  textAlign: 'center',
  overflow: 'scroll',
};
